import * as React from 'react';
import { CustomDivider, PriceLigne, useL2DTheme } from '@europrocurement/flexy-components';
import {
    type CollectionDeDossierCollectiondossierRead,
    type FproPrestationCollectiondossierRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { FaOptionIcon, waitFormalisteIcon } from '@europrocurement/l2d-icons';
import { Box, Typography } from '@mui/material';

type RecapArticlesLineProps = {
    montant: number;
    prixEnAttente: boolean;
};
type RecapArticlesLinesProps = {
    PubPubns: RecapArticlesLineProps;
    Vac: RecapArticlesLineProps;
    FrnFrs: RecapArticlesLineProps;
    Autre: RecapArticlesLineProps;
    withPrice: boolean;
};

type RecapLignesArticlesProps = {
    dossierPrestations: FproPrestationCollectiondossierRead[] | undefined;
    collection: CollectionDeDossierCollectiondossierRead;
};
const RecapLignesArticles: React.FunctionComponent<RecapLignesArticlesProps> = function ({
    dossierPrestations,
    collection,
}) {
    const { palette } = useL2DTheme();

    const FrenchPrice = (price: number | string | null | undefined) => {
        if (!price) {
            return '';
        }
        let value = price;
        if (typeof price === 'string') {
            value = parseFloat(price);
        }

        return (Math.floor((value as number) * 100) / 100).toFixed(2).replace('.', ',');
        // return (Math.floor(value * 100) / 100).toFixed(2).toString().replace('.', ',');
    };

    const reduceLines = function (): RecapArticlesLinesProps {
        const montantReduit: RecapArticlesLinesProps = {
            PubPubns: { montant: 0, prixEnAttente: false },
            Vac: { montant: 0, prixEnAttente: false },
            FrnFrs: { montant: 0, prixEnAttente: false },
            Autre: { montant: 0, prixEnAttente: false },
            withPrice: false,
        };

        if (!dossierPrestations || dossierPrestations.length === 0) {
            return montantReduit;
        }
        dossierPrestations.map((prestation) => {
            if (prestation.articles) {
                prestation.articles.map((article) => {
                    if (article.tarif && !article.fMasqueCalcul && article.factArticle.rubArticle) {
                        switch (article.factArticle.rubArticle) {
                            case 'FRN':
                            case 'FRS':
                                montantReduit.FrnFrs.montant +=
                                    Number(article.tarif) * Number(article.quantite);
                                montantReduit.withPrice = true;
                                break;
                            case 'VAC':
                                montantReduit.Vac.montant +=
                                    Number(article.tarif) * Number(article.quantite);
                                montantReduit.withPrice = true;
                                break;
                            case 'PUB':
                            case 'PUBNS':
                                montantReduit.PubPubns.montant +=
                                    Number(article.tarif) * Number(article.quantite);
                                montantReduit.withPrice = true;
                                break;
                            default:
                                montantReduit.Autre.montant +=
                                    Number(article.tarif) * Number(article.quantite);
                                montantReduit.withPrice = true;
                                break;
                        }
                    } else if (!article.tarif) {
                        switch (article.factArticle.rubArticle) {
                            case 'FRN':
                            case 'FRS':
                                montantReduit.FrnFrs.prixEnAttente = true;
                                break;
                            case 'VAC':
                                montantReduit.Vac.prixEnAttente = true;
                                break;
                            case 'PUB':
                            case 'PUBNS':
                                montantReduit.PubPubns.prixEnAttente = true;
                                break;
                            default:
                                montantReduit.Autre.prixEnAttente = true;
                                break;
                        }
                    }
                    return montantReduit;
                }, []);
            }
            return montantReduit;
        });

        return montantReduit;
    };

    const lignesArticle: RecapArticlesLinesProps = React.useMemo(
        () => reduceLines(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dossierPrestations],
    );

    return (
        <Box
            className="information-card"
            sx={{
                backgroundColor: palette.background.dark,
                // boxShadow: `0px 2px 2.5px 0px ${palette.background.shadow}`,
                padding: '24px  32px',
            }}
        >
            <Typography
                variant="l2dbodyl"
                sx={{ fontWeight: 400 }}
            >
                Estimation
            </Typography>
            <Box sx={{ marginTop: '16px' }}>
                {lignesArticle.FrnFrs.montant ? (
                    <PriceLigne
                        label="Frais de registre"
                        price={`${FrenchPrice(lignesArticle.FrnFrs.montant)} €${
                            lignesArticle.FrnFrs.prixEnAttente ? (
                                <FaOptionIcon
                                    size="sm"
                                    {...waitFormalisteIcon.props}
                                />
                            ) : (
                                ''
                            )
                        }`}
                    />
                ) : null}
                {lignesArticle.FrnFrs.prixEnAttente && !lignesArticle.FrnFrs.montant && (
                    <PriceLigne
                        label="Frais de registre"
                        price={
                            <FaOptionIcon
                                size="sm"
                                {...waitFormalisteIcon.props}
                            />
                        }
                    />
                )}
                {lignesArticle.PubPubns.montant ? (
                    <PriceLigne
                        label="Annonce légale"
                        price={`${FrenchPrice(lignesArticle.PubPubns.montant)} €${
                            lignesArticle.FrnFrs.prixEnAttente ? (
                                <FaOptionIcon
                                    size="sm"
                                    {...waitFormalisteIcon.props}
                                />
                            ) : (
                                ''
                            )
                        }`}
                    />
                ) : null}
                {lignesArticle.PubPubns.prixEnAttente && !lignesArticle.PubPubns.montant && (
                    <PriceLigne
                        label="Annonce légale"
                        price={
                            <FaOptionIcon
                                size="sm"
                                {...waitFormalisteIcon.props}
                            />
                        }
                    />
                )}
                {lignesArticle.Vac.montant ? (
                    <PriceLigne
                        label="Honoraires"
                        price={
                            <>
                                {`${FrenchPrice(lignesArticle.Vac.montant)} €`}
                                {lignesArticle.FrnFrs.prixEnAttente ? (
                                    <FaOptionIcon
                                        size="sm"
                                        {...waitFormalisteIcon.props}
                                    />
                                ) : null}
                            </>
                        }
                        priceTypographyProps={{
                            display: 'inline-flex',
                        }}
                    />
                ) : null}
                {lignesArticle.Vac.prixEnAttente && !lignesArticle.Vac.montant && (
                    <PriceLigne
                        label="Honoraires"
                        price={
                            <FaOptionIcon
                                size="sm"
                                {...waitFormalisteIcon.props}
                            />
                        }
                    />
                )}
                {lignesArticle.Autre.montant ? (
                    <PriceLigne
                        label="Divers"
                        price={`${FrenchPrice(lignesArticle.Autre.montant)} €${
                            lignesArticle.FrnFrs.prixEnAttente ? (
                                <FaOptionIcon
                                    size="sm"
                                    {...waitFormalisteIcon.props}
                                />
                            ) : (
                                ''
                            )
                        }`}
                    />
                ) : null}
                {lignesArticle.Autre.prixEnAttente && !lignesArticle.Autre.montant && (
                    <PriceLigne
                        label="Divers"
                        price={
                            <FaOptionIcon
                                size="sm"
                                {...waitFormalisteIcon.props}
                            />
                        }
                    />
                )}

                {lignesArticle.FrnFrs.prixEnAttente ||
                lignesArticle.PubPubns.prixEnAttente ||
                lignesArticle.Vac.prixEnAttente ||
                lignesArticle.Autre.prixEnAttente ? (
                    <Box
                        sx={{
                            marginTop: '15px',
                            background: 'pink',
                            alignItems: 'center',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            backgroundColor: palette.grey[200],
                        }}
                    >
                        <Typography variant="l2dbodyxs">
                            Sous réserve de l&apos;étude de votre dossier par votre formaliste
                        </Typography>
                    </Box>
                ) : null}
            </Box>
            {lignesArticle.withPrice ? (
                <>
                    <CustomDivider style={{ opacity: '10%', height: '1px' }} />
                    <PriceLigne
                        labelTypographyProps={{
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                        priceTypographyProps={{
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                        label="Prix HT"
                        price={`${FrenchPrice(collection.ht)} €`}
                    />
                    <PriceLigne
                        labelTypographyProps={{
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                        priceTypographyProps={{
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                        label="TVA"
                        price={`${FrenchPrice(collection.tva)} €`}
                    />
                    <CustomDivider style={{ opacity: '10%', height: '1px' }} />
                    <PriceLigne
                        labelTypographyProps={{
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                        priceTypographyProps={{
                            variant: 'l2dh6',
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                        label="Prix TTC"
                        price={`${FrenchPrice(collection.ttc)} €`}
                        sx={{ paddingTop: '12px', paddingBottom: '12px' }}
                    />
                </>
            ) : null}
        </Box>
    );
};

export default RecapLignesArticles;
